import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AtlasConfigService } from '@galaxy/atlas/core';
import { InternalAtlasService } from '../atlas/atlas.service';
import { AppConfig, AppConfigService } from '../core/app-config.service';
import { CustomerVoiceRouterService } from '../core/cv-router.service';
import { AtlasData } from './atlas.service';
import { partnerId as injectedPartnerId } from '../../globals';

const PAM_SERVICE_PROVIDER_ID = 'customer-voice';
const PAM_ENTITLEMENT_ID = 'partner-access';

@Component({
  selector: 'app-atlas',
  templateUrl: './atlas.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AtlasComponent implements OnInit {
  partnerId$: Observable<string>;
  marketId$: Observable<string>;
  accountGroupId$: Observable<string>;
  customerVoiceId$: Observable<string>;
  isAtlasEnabled$: Observable<boolean>;

  atlasData$: Observable<AtlasData>;

  private readonly atlasConfigService = inject(AtlasConfigService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private appConfig: AppConfigService,
    private cvRouter: CustomerVoiceRouterService,
    public router: Router,
    private readonly atlasInternalService: InternalAtlasService,
  ) {}

  ngOnInit(): void {
    this.partnerId$ = this.appConfig.appConfig.pipe(map((config: AppConfig) => config.partnerId));
    this.marketId$ = this.appConfig.appConfig.pipe(map((config: AppConfig) => config.marketId));
    this.customerVoiceId$ = this.appConfig.appConfig.pipe(
      map((config: AppConfig) => config.vendorCenterAppId),
      startWith(''),
    );
    this.accountGroupId$ = this.cvRouter.accountGroupId;

    this.atlasData$ = combineLatest([this.partnerId$, this.marketId$, this.accountGroupId$]).pipe(
      map(([partnerID, marketID, accountGroupID]) => ({
        partnerID,
        marketID,
        accountGroupID,
      })),
    );
    this.isAtlasEnabled$ = this.atlasInternalService.isAtlasEnabled$;

    this.initializeAtlasConfigForPam();
  }

  // Seed AtlasConfig from route + injected partnerId so the PAM check resolves even when app-config 403s.
  private initializeAtlasConfigForPam(): void {
    if (!injectedPartnerId) {
      return;
    }
    this.accountGroupId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((accountGroupId) => {
      this.atlasConfigService.initialize(
        PAM_SERVICE_PROVIDER_ID,
        injectedPartnerId,
        '',
        accountGroupId,
        PAM_SERVICE_PROVIDER_ID,
        '',
        PAM_ENTITLEMENT_ID,
      );
    });
  }
}
